import { StateCreator } from "zustand";
import type { MeasureWidget, ModalDeclaration, StatisticWidget, StepDescription } from "./types.ts";
import { ReactNode } from "react";
import { MENU_ITEMS, STEPS } from "./types.ts";
import { PROPERTIES_DEFAULT, PropertySlice } from "./property-slice.ts";
import { ApiSlice } from "./api-slice.ts";

let menu_timeout : number | undefined = undefined;

export interface AppSlice {
  modal_counter: number;
  set_selected_menu_item: (value: string) => void;
  selected_menu_item?: string;
  add_statistic_widget: (widget: StatisticWidget) => void;
  statistic_widgets: Array<StatisticWidget>;
  add_measures_widget: (widget: MeasureWidget) => void;
  measures_widgets: Array<MeasureWidget>;
  push_modal: (modal: ModalDeclaration) => void;
  push_modal_non_close: (modal: ReactNode) => void;
  push_modal_closeable: (modal: ReactNode) => void;
  clear_modal: () => void;
  pop_modal: () => ModalDeclaration | undefined;
  modals: Array<ModalDeclaration>;
  is_modal_active: () => boolean;
  active_modal: () => ModalDeclaration;
  keep_menu_open: boolean;
  help_text?: string;
  set_help_text: (value: string) => void;
  // menu_items: Array<{
  //   id: string;
  //   label: string;
  // }>;
  open_menu: () => void;
  close_menu: () => void;
  current_step: StepDescription;
  current_step_number: number,
  last_step_number: number,
  steps: Array<StepDescription>;
  set_step: (value: number) => void;
  increment_step: () => void;
  decrement_step: () => void;

  is_last_step: () => boolean;
  is_step_id: (string: string) => boolean;
  session_token: undefined | string;
  // api_login: () => Promise<void>;
  // api_basic_data: () => Promise<void>;
  // api_calculate_energy_costs: () => Promise<void>;
  reset: () => Promise<void>;
}

export const createAppSlice: StateCreator<AppSlice & ApiSlice & PropertySlice, [], [], AppSlice> = (
  set,
  get,
) => ({
  modal_counter:0,
  set_selected_menu_item: (value) => {
    clearTimeout(menu_timeout);
    menu_timeout = setTimeout(()=>{
      if(get().modals.length == 0){
          set((_state) => ({ keep_menu_open: false }));
      }
    }, 500);    
    set((_state) => ({ selected_menu_item: value, keep_menu_open: true  }));
  },
  selected_menu_item: undefined,
  add_statistic_widget: (widget) => {
    if (!get().statistic_widgets.some((w) => widget.key == w.key)) {
      set((state) => ({
        statistic_widgets: [...state.statistic_widgets, widget],
      }));
    }
  },
  statistic_widgets: [],
  add_measures_widget: (widget) => {
    if (!get().measures_widgets.some((w) => widget.key == w.key)) {
      set((state) => ({
        measures_widgets: [...state.measures_widgets, widget],
      }));
    }
  },
  measures_widgets: [],

  clear_properties_changed: () => {
    set((_) => ({ properties_changed: [] }));
  },

  push_modal: (modal) => {
    set((state) => ({ modal_counter: get().modal_counter + 1, modals: [...state.modals, modal] }));
  },
  push_modal_non_close: (modal) => {
    set((state) => ({ modal_counter: get().modal_counter + 1, modals: [...state.modals, {close_on_click_background: false, modal: () => modal}] }));
  },
  push_modal_closeable: (modal) => {
    set((state) => ({ modal_counter: get().modal_counter + 1, modals: [...state.modals, {close_on_click_background: true, modal: () => modal}] }));
  },
  pop_modal: () => {
    const modals = [...get().modals];
    if(modals.length == 0){
      clearInterval(menu_timeout);
      menu_timeout = setTimeout(()=>{
        if(get().modals.length == 0){
            set((_state) => ({ keep_menu_open: false }));
        }
      }, 500); 
    }
    const popped = modals.pop();
    set((_state) => ({ modal_counter: get().modal_counter + 1, modals }));
    return popped;
  },
  is_modal_active: () => {
    return get().modals.length > 0;
  },
  active_modal: () => {
    return get().modals[get().modals.length - 1];
  },
  clear_modal: () => {
    clearInterval(menu_timeout);
    menu_timeout = setTimeout(()=>{
      if(get().modals.length == 0){
          set((_state) => ({ keep_menu_open: false }));
      }
    }, 500);   
    set((_state) => ({ modals: [] }));
  },
  modals: [],
  keep_menu_open: false,
  close_menu: () => {
    set((_state) => ({ selected_menu_item: undefined, keep_menu_open: false }));
  },
  open_menu: () => {
    set((_state) => ({ selected_menu_item: MENU_ITEMS[0].id}));
  },
  set_help_text: (value) => {
    set((_state) => ({ help_text: value}));
  },

  current_step: STEPS[0],
  current_step_number: 0,
  last_step_number: 0,
  steps: STEPS,
  set_step: (value: number) => {
    set((state) => ({ last_step_number:state.current_step_number, current_step_number: value, current_step: STEPS[value]}));
  },
  increment_step: () => {
    get().set_step(Math.min(get().current_step_number + 1,get().steps.length -1));
  },
  decrement_step: () => {
    get().set_step(Math.max(get().current_step_number - 1,0));
  },
  is_last_step: ()=>{
    return get().current_step_number == get().steps.length - 1;
  },
  is_step_id: (string: string) => {
    return get().current_step.id == string;
  },

  reset: async () => {
    set((_)=>({...PROPERTIES_DEFAULT, last_step_number:0, current_step_number: 0, current_step: STEPS[0]}));
    await get().api_login();
  }
});
